import { Loader2 } from "lucide-react";
import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, useToast } from "randmarcomps";
import { ManufacturerCategory, useDeleteV4ManufacturerByRouteManufacturerIdCategoryAndCategoryCodeMutation, useGetV4ManufacturerByRouteManufacturerIdAccountQuery, usePostV4ManufacturerByRouteManufacturerIdCategoryAndCategoryCodeResetMutation } from "randmar-api-client";
import { useManufacturer } from "../../contexts/WithManufacturer";
import Path from "../../routes/Path";
import { Category as LegacyCategory } from "../../stores/category/Category";

interface IProps {
  category: ManufacturerCategory | LegacyCategory;
  disabled?: boolean;
  buttonTitle?: string;
  text?: string;
  children: React.ReactNode;
}

const isLegacyCategory = (cat: ManufacturerCategory | LegacyCategory): cat is LegacyCategory => 'categoryCode' in cat;

const DeleteCategoryWrapper: React.FC<IProps> = ({ category, disabled, buttonTitle, text, children }) => {
  const { applicationId } = useManufacturer();
  const [open, setOpen] = useState(false);
  const [redirect, setRedirect] = useState(false);
  const { toast } = useToast();

  const [deleteCategoryMutation, { isLoading: isDeleting }] = useDeleteV4ManufacturerByRouteManufacturerIdCategoryAndCategoryCodeMutation();
  const [resetCategoryMutation, { isLoading: isResetting }] = usePostV4ManufacturerByRouteManufacturerIdCategoryAndCategoryCodeResetMutation();

  const { data: manufacturer } = useGetV4ManufacturerByRouteManufacturerIdAccountQuery({ routeManufacturerId: applicationId });

  const categoryCode = (isLegacyCategory(category) ? category.categoryCode : category.CategoryCode) || "";
  const categoryName = (isLegacyCategory(category) ? category.categoryName : category.CategoryName) || "";

  const loading = isDeleting || isResetting;

  const handleDeleteCategory = async () => {
    try {
      await deleteCategoryMutation({
        routeManufacturerId: applicationId,
        categoryCode: categoryCode
      }).unwrap();
      setRedirect(true);
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: "Failed to delete category." });
    }
  };

  const handleResetCategory = async () => {
    try {
      await resetCategoryMutation({
        routeManufacturerId: applicationId,
        categoryCode: categoryCode
      }).unwrap();
      toast({ variant: "success", title: "Success", description: `Category ${categoryName} was successfully reset.` });
      setOpen(false);
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: `Couldn't reset the category ${categoryName}.` });
    }
  };

  const handleCancel = () => {
    setOpen(false);
  };

  if (redirect) return <Navigate to={Path.categories(applicationId)} />;

  const isResetAction = buttonTitle === "Reset to Default Settings";
  const confirmationText =
    text ??
    (isResetAction
      ? `Are you sure you want to reset the category "${categoryName}" to its default settings?`
      : `Are you sure you want to delete the category "${categoryName}"? This action cannot be undone.`);

  const loadingText = isResetAction ? `Resetting ${categoryName}...` : `Deleting ${categoryName}...`;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !loading && setOpen(isOpen)}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-md bg-white">
        <DialogHeader>
          <DialogTitle>{buttonTitle}</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex flex-col items-center justify-center space-y-4 p-8">
            <p>{loadingText}</p>
            <Loader2 className="h-8 w-8 animate-spin text-purple-800" />
          </div>
        ) : (
          <>
            <div className="p-4">
              <p>{confirmationText}</p>
            </div>

            {isResetAction && manufacturer && (
              <div className="px-4 pb-4">
                <div className="border border-slate-200 rounded-lg w-full p-4 space-y-2 bg-slate-50">
                  <h4 className="text-sm font-semibold text-slate-900 mb-2">The following default settings will be applied:</h4>
                  <div className="flex justify-between items-center text-sm">
                    <p className="font-medium text-slate-600">Lead Time</p>
                    <p className="text-slate-800 font-mono">{manufacturer.DefaultLeadTime} days</p>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <p className="font-medium text-slate-600">Minimum Order</p>
                    <p className="text-slate-800 font-mono">${manufacturer.DefaultMinimumOrder}</p>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <p className="font-medium text-slate-600">Weeks of Stocking</p>
                    <p className="text-slate-800 font-mono">{manufacturer.DefaultWeeksOfStocking} weeks</p>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                disabled={disabled || loading}
                variant={buttonTitle === "Reset to Default Settings" ? "default" : "destructive"}
                onClick={buttonTitle === "Reset to Default Settings" ? handleResetCategory : handleDeleteCategory}
              >
                Confirm
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DeleteCategoryWrapper;
